import { useEffect, useMemo, useState, type ReactElement } from "react";
import {
  Button,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Select,
  Spinner,
  Switch,
  Textarea,
  makeStyles,
  mergeClasses,
  tokens,
} from "@fluentui/react-components";
import { Box24Regular, DocumentText24Regular, Food24Regular, Print24Regular, Send24Regular } from "@fluentui/react-icons";
import { TIPI, type Tipo } from "@app/schema";
import { useData } from "../../data/DataProvider";
import { articoliPerStampante, categoriePerTipo, useCatalogo, type ArticoloRef } from "../../data/catalogo";
import { inviaRichiesta, sceltePrecedenti, valoreProposto, type NuovaRichiesta as Nuova } from "../../data/richieste";
import { formatEuro, parseNumber } from "../../lib/format";
import { goBack, replace } from "../../lib/nav";
import { Shell } from "../../components/Shell";
import { Section } from "../../components/Section";
import { ChipGroup } from "../../components/ChipGroup";
import { ErrorState, describeError } from "../../components/ErrorState";

const useStyles = makeStyles({
  tipi: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: tokens.spacingHorizontalS },
  tipo: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: tokens.spacingVerticalXS,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    fontFamily: "inherit",
    fontSize: tokens.fontSizeBase300,
    cursor: "pointer",
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "1px" },
  },
  tipoIcon: { fontSize: "28px", color: tokens.colorBrandForeground1 },
  selected: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground2,
    border: `1px solid ${tokens.colorBrandStroke1}`,
    fontWeight: tokens.fontWeightSemibold,
  },
  articoli: { display: "flex", flexDirection: "column", gap: tokens.spacingVerticalS },
  articolo: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    textAlign: "left",
    padding: tokens.spacingHorizontalM,
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    fontFamily: "inherit",
    fontSize: tokens.fontSizeBase300,
    cursor: "pointer",
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "1px" },
  },
  articoloNome: { fontWeight: tokens.fontWeightSemibold },
  articoloMeta: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  urgente: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  hint: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  select: { width: "100%", minWidth: 0 },
  valore: { display: "flex", justifyContent: "space-between", fontWeight: tokens.fontWeightSemibold },
});

const ICONE: Record<Tipo, ReactElement> = {
  Cancelleria: <DocumentText24Regular />,
  Mensa: <Food24Regular />,
  Stampanti: <Print24Regular />,
  Generica: <Box24Regular />,
};
const ETICHETTE: Record<Tipo, string> = {
  Cancelleria: "Cancelleria",
  Mensa: "Mensa e acqua",
  Stampanti: "Materiale stampanti",
  Generica: "Altro",
};

type Errors = Partial<Record<"tipo" | "servizio" | "stampante" | "articolo" | "categoria" | "descrizione" | "quantita", string>>;

export function NuovaRichiesta() {
  const s = useStyles();
  const data = useData();
  const prev = useMemo(() => sceltePrecedenti(data), [data]);
  const { catalogo, error: catalogoError } = useCatalogo(data);

  const [tipo, setTipo] = useState<Tipo>();
  const [servizio, setServizio] = useState<string | undefined>(prev.servizio);
  const [stampanteId, setStampanteId] = useState<number | undefined>(prev.stampanteId);
  const [articoloId, setArticoloId] = useState<number>();
  const [categoria, setCategoria] = useState<string>();
  const [descrizione, setDescrizione] = useState("");
  const [quantita, setQuantita] = useState("1");
  const [urgente, setUrgente] = useState(false);
  const [note, setNote] = useState("");
  const [errors, setErrors] = useState<Errors>({});
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string>();

  // Le scelte ricordate potrebbero non esistere più (servizio o stampante disattivati)
  useEffect(() => {
    if (!catalogo) return;
    setServizio((cur) => (cur && catalogo.servizi.includes(cur) ? cur : undefined));
    setStampanteId((cur) => (cur && catalogo.stampanti.some((x) => x.id === cur) ? cur : undefined));
  }, [catalogo]);

  const categorie = catalogo && tipo ? categoriePerTipo(catalogo, tipo) : [];
  const articoli = catalogo && stampanteId ? articoliPerStampante(catalogo, stampanteId) : [];
  const stampante = catalogo?.stampanti.find((x) => x.id === stampanteId);
  const articolo: ArticoloRef | undefined = articoli.find((a) => a.id === articoloId);
  const q = parseNumber(quantita);
  const valore = articolo && q ? valoreProposto(q, articolo.prezzoNetto) : undefined;

  const clear = (k: keyof Errors) => setErrors((e) => ({ ...e, [k]: undefined }));

  function scegliTipo(t: Tipo) {
    setTipo(t);
    clear("tipo");
    // la categoria scelta per un altro tipo potrebbe non valere più
    if (catalogo && categoria && !categoriePerTipo(catalogo, t).includes(categoria)) setCategoria(undefined);
  }

  function validate(): Nuova | undefined {
    const e: Errors = {};
    if (!tipo) e.tipo = "Scegli cosa ti serve.";
    if (!servizio) e.servizio = "Scegli il servizio.";
    if (q === undefined || q <= 0) e.quantita = "Indica una quantità maggiore di zero.";
    if (tipo === "Stampanti") {
      if (!stampante) e.stampante = "Scegli la stampante.";
      else if (!articolo) e.articolo = articoli.length ? "Scegli l'articolo." : undefined;
    } else if (tipo) {
      if (!categoria) e.categoria = "Scegli la categoria.";
      if (!descrizione.trim()) e.descrizione = "Descrivi cosa serve.";
    }
    setErrors(e);
    if (Object.values(e).some(Boolean) || !tipo || !servizio || !q) return undefined;
    const base = { servizio, quantita: q, urgente, note };
    if (tipo === "Stampanti") return stampante && articolo ? { ...base, tipo, stampante, articolo } : undefined;
    return categoria ? { ...base, tipo, categoria, descrizione } : undefined;
  }

  async function submit() {
    const r = validate();
    if (!r) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setSendError(undefined);
    setSending(true);
    try {
      const id = await inviaRichiesta(data, r);
      replace(`/inviata/${id}`);
    } catch (e) {
      setSendError(describeError(e).detail);
      setSending(false);
    }
  }

  if (catalogoError)
    return (
      <Shell title="Nuova richiesta" onBack={goBack}>
        <ErrorState error={catalogoError} />
      </Shell>
    );

  return (
    <Shell
      title="Nuova richiesta"
      onBack={sending ? undefined : goBack}
      footer={
        <Button
          appearance="primary"
          size="large"
          icon={sending ? <Spinner size="tiny" /> : <Send24Regular />}
          style={{ flexGrow: 1 }}
          onClick={submit}
          disabled={
            sending || !catalogo || (tipo === "Stampanti" && (catalogo.stampanti.length === 0 || (!!stampante && articoli.length === 0)))
          }
        >
          {sending ? "Invio…" : "Invia richiesta"}
        </Button>
      }
    >
      {sendError && (
        <MessageBar intent="error">
          <MessageBarBody>{sendError}</MessageBarBody>
        </MessageBar>
      )}

      {!catalogo ? (
        <Spinner style={{ marginTop: 48 }} />
      ) : (
        <>
          <Section title="Cosa ti serve">
            <Field validationMessage={errors.tipo}>
              <div className={s.tipi} role="radiogroup" aria-label="Tipo di richiesta">
                {TIPI.map((t) => (
                  <button
                    key={t}
                    type="button"
                    role="radio"
                    aria-checked={t === tipo}
                    className={mergeClasses(s.tipo, t === tipo && s.selected)}
                    onClick={() => scegliTipo(t)}
                  >
                    <span className={s.tipoIcon}>{ICONE[t]}</span>
                    {ETICHETTE[t]}
                  </button>
                ))}
              </div>
            </Field>
          </Section>

          <Section title="Per quale servizio">
            <Field validationMessage={errors.servizio}>
              <ChipGroup
                ariaLabel="Servizio"
                options={catalogo.servizi}
                value={servizio}
                onChange={(v) => {
                  setServizio(v);
                  clear("servizio");
                }}
              />
            </Field>
          </Section>

          {tipo === "Stampanti" && (
            <Section title="Materiale per stampanti">
              {catalogo.stampanti.length === 0 ? (
                <MessageBar intent="warning">
                  <MessageBarBody>
                    Nessuna stampante a catalogo. Chiedi ai gestori degli acquisti di aggiornare il catalogo, oppure scegli "Altro" e
                    descrivi cosa serve.
                  </MessageBarBody>
                </MessageBar>
              ) : (
                <Field label="Stampante" required validationMessage={errors.stampante}>
                  <Select
                    className={s.select}
                    select={{ style: { minWidth: 0, textOverflow: "ellipsis" } }}
                    size="large"
                    value={stampanteId ? String(stampanteId) : ""}
                    onChange={(_, d) => {
                      setStampanteId(d.value ? Number(d.value) : undefined);
                      setArticoloId(undefined);
                      clear("stampante");
                    }}
                  >
                    <option value="">Scegli la stampante…</option>
                    {catalogo.stampanti.map((st) => (
                      <option key={st.id} value={st.id}>
                        {st.nome}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}

              {stampante && (
                <Field label="Articolo" required validationMessage={errors.articolo}>
                  {articoli.length === 0 ? (
                    <MessageBar intent="warning">
                      <MessageBarBody>
                        Per questa stampante non ci sono articoli a catalogo. Scegli "Altro" e descrivi cosa serve.
                      </MessageBarBody>
                    </MessageBar>
                  ) : (
                    <div className={s.articoli} role="radiogroup" aria-label="Articolo">
                      {articoli.map((a) => (
                        <button
                          key={a.id}
                          type="button"
                          role="radio"
                          aria-checked={a.id === articoloId}
                          className={mergeClasses(s.articolo, a.id === articoloId && s.selected)}
                          onClick={() => {
                            setArticoloId(a.id);
                            clear("articolo");
                          }}
                        >
                          <span className={s.articoloNome}>{a.nome}</span>
                          <span className={s.articoloMeta}>
                            {[a.codice, a.prezzoNetto != null ? `${formatEuro(a.prezzoNetto)}${a.unita ? ` / ${a.unita}` : ""}` : undefined]
                              .filter(Boolean)
                              .join(" · ")}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </Field>
              )}
            </Section>
          )}

          {tipo && tipo !== "Stampanti" && (
            <Section title="Dettagli">
              <Field label="Categoria" required validationMessage={errors.categoria}>
                <ChipGroup
                  ariaLabel="Categoria"
                  options={categorie}
                  value={categoria}
                  onChange={(v) => {
                    setCategoria(v);
                    clear("categoria");
                  }}
                />
              </Field>
              <Field label="Descrizione" required validationMessage={errors.descrizione}>
                <Textarea
                  size="large"
                  rows={3}
                  resize="vertical"
                  value={descrizione}
                  placeholder="Cosa serve: tipo, formato, marca se importante"
                  onChange={(_, d) => {
                    setDescrizione(d.value);
                    clear("descrizione");
                  }}
                />
              </Field>
            </Section>
          )}

          {tipo && (
            <Section title="Quantità e priorità">
              <Field label="Quantità" required validationMessage={errors.quantita}>
                <Input
                  size="large"
                  inputMode="decimal"
                  value={quantita}
                  onChange={(_, d) => {
                    setQuantita(d.value);
                    clear("quantita");
                  }}
                />
              </Field>
              {valore != null && (
                <div className={s.valore}>
                  <span>Valore stimato</span>
                  <span>{formatEuro(valore)} + IVA</span>
                </div>
              )}
              <div className={s.urgente}>
                <div>
                  <div>Urgente</div>
                  <div className={s.hint}>Materiale esaurito o servizio bloccato</div>
                </div>
                <Switch checked={urgente} onChange={(_, d) => setUrgente(d.checked)} aria-label="Urgente" />
              </div>
              <Field label="Note per il gestore">
                <Textarea size="large" rows={2} resize="vertical" value={note} onChange={(_, d) => setNote(d.value)} />
              </Field>
            </Section>
          )}
        </>
      )}
    </Shell>
  );
}
